import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getContact, convertContactToLead } from '../api';
import toast from 'react-hot-toast';
import {
  SkeletonBox,
  SkeletonText,
  SideDrawer,
  Modal,
  UserIcon,
} from '@/components';
import { formatName } from '@/common/utils';
import { customerTypeLabel } from '../utils/customerTypeLabel';

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-[#868686]">{label}</span>
      <span className="text-sm font-semibold text-[#1F2241] text-right break-words">
        {value === undefined || value === null || value === '' ? '-' : value}
      </span>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5 flex flex-col gap-3">
      <h3 className="text-[18px] font-medium text-[#50AB3D]">{title}</h3>
      <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3.5">
        {children}
      </div>
    </div>
  );
}

function formatDateTime(dateString) {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return d.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function ContactDetailsModal({ isOpen, onClose, contactId, companyId }) {
  const queryClient = useQueryClient();
  const [showConfirm, setShowConfirm] = useState(false);
  const [cachedContact, setCachedContact] = useState(null);

  const { data: contact, isLoading: isQueryLoading } = useQuery({
    queryKey: ['contactDetails', companyId, contactId],
    queryFn: () => getContact({ companyId, clientId: contactId }),
    enabled: isOpen && !!companyId && !!contactId,
  });

  useEffect(() => {
    if (contact) {
      setCachedContact(contact);
    }
  }, [contact]);

  const activeContact =
    contact || (isOpen && isQueryLoading ? null : cachedContact);
  const isLoading = isOpen && (isQueryLoading || !activeContact);

  const convertMutation = useMutation({
    mutationFn: () => convertContactToLead({ companyId, clientId: contactId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', companyId] });
      queryClient.invalidateQueries({
        queryKey: ['contactDetails', companyId, contactId],
      });
      onClose();
    },
  });

  const customerTypeName = customerTypeLabel(activeContact);

  const displayPhone =
    activeContact?.phone_normalized ||
    (activeContact?.country_code && activeContact?.phone
      ? `${activeContact.country_code} ${activeContact.phone}`
      : activeContact?.phone);

  const isLead = Boolean(activeContact?.is_lead || activeContact?.lead_id);

  return (
    <SideDrawer
      className="contacts-panel"
      isOpen={isOpen}
      onClose={onClose}
      width="md"
      showClose={false}
      header={
        <div className="flex items-start justify-between w-full gap-3">
          <div className="flex items-start gap-3 min-w-0 pr-2 flex-1">
            {isLoading ? (
              <SkeletonBox
                className="w-9 h-9 shrink-0 mt-0.5"
                rounded="rounded-full"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-surface-mint flex items-center justify-center shrink-0 mt-0.5">
                <UserIcon className="w-5 h-5 text-primary" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              {isLoading ? (
                <div className="flex flex-col gap-1.5 pt-0.5">
                  <SkeletonText width="w-36" height="h-5" />
                  <SkeletonText width="w-20" height="h-3.5" />
                </div>
              ) : (
                <>
                  <h2 className="text-base font-bold text-text-primary break-words leading-snug">
                    {formatName(activeContact?.name || 'Unnamed Contact')}
                  </h2>
                  <p className="text-sm text-text-muted break-words mt-0.5">
                    {customerTypeName}
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!isLoading && !isLead && (
              <button
                type="button"
                onClick={() => setShowConfirm(true)}
                disabled={convertMutation.isPending}
                className="px-3 py-1.5 h-8 rounded-[8px] bg-[#50AB3D] text-white text-xs font-semibold flex items-center justify-center hover:bg-[#408a31] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                {convertMutation.isPending
                  ? 'Converting...'
                  : 'Convert to Lead'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {isLoading ? (
          <>
            {/* Contact Details Skeleton */}
            <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5 flex flex-col gap-3">
              <SkeletonText width="w-32" height="h-5" />
              <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3.5">
                <div className="flex items-center justify-between gap-3">
                  <SkeletonText width="w-16" height="h-4" />
                  <SkeletonText width="w-32" height="h-4" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <SkeletonText width="w-24" height="h-4" />
                  <SkeletonText width="w-12" height="h-4" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <SkeletonText width="w-20" height="h-4" />
                  <SkeletonText width="w-10" height="h-4" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <SkeletonText width="w-28" height="h-4" />
                  <SkeletonText width="w-20" height="h-4" />
                </div>
              </div>
            </div>

            {/* Messaging Status Skeleton */}
            <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5 flex flex-col gap-3">
              <SkeletonText width="w-36" height="h-5" />
              <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3.5">
                <div className="flex items-center justify-between gap-3">
                  <SkeletonText width="w-36" height="h-4" />
                  <SkeletonText width="w-12" height="h-4" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <SkeletonText width="w-32" height="h-4" />
                  <SkeletonText width="w-28" height="h-4" />
                </div>
              </div>
            </div>

            {/* Additional Info Skeleton */}
            <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5 flex flex-col gap-3">
              <SkeletonText width="w-32" height="h-5" />
              <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3.5">
                <div className="flex items-center justify-between gap-3">
                  <SkeletonText width="w-20" height="h-4" />
                  <SkeletonText width="w-32" height="h-4" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <SkeletonText width="w-20" height="h-4" />
                  <SkeletonText width="w-32" height="h-4" />
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <DetailSection title="Contact Details">
              <DetailRow label="Phone" value={displayPhone} />
              <DetailRow label="Email" value={activeContact?.email} />
              <DetailRow
                label="Country Code"
                value={activeContact?.country_code}
              />
              <DetailRow
                label="Country ISO"
                value={activeContact?.country_iso}
              />
              <DetailRow label="Customer Type" value={customerTypeName} />
            </DetailSection>

            <DetailSection title="Messaging Status">
              <DetailRow
                label="24h Window Active"
                value={
                  activeContact?.last_send_by_24_hours ? (
                    <span className="text-status-success">Yes</span>
                  ) : (
                    <span className="text-status-error">No</span>
                  )
                }
              />
              <DetailRow
                label="Last Message Sent"
                value={formatDateTime(activeContact?.last_message_sent_at)}
              />
            </DetailSection>

            <DetailSection title="Additional Info">
              <DetailRow
                label="Created At"
                value={formatDateTime(activeContact?.created_at)}
              />
              <DetailRow
                label="Updated At"
                value={formatDateTime(activeContact?.updated_at)}
              />
            </DetailSection>
          </>
        )}
      </div>

      <Modal
        isOpen={showConfirm}
        onClose={() => !convertMutation.isPending && setShowConfirm(false)}
        size="sm"
        showClose={false}
        panelClassName="p-5 sm:p-6 rounded-[24px]"
        zIndexClass="z-[110]"
      >
        <div className="flex flex-col gap-4 text-center">
          <div className="w-12 h-12 rounded-full bg-[#E8F3E5] text-[#50AB3D] flex items-center justify-center mx-auto shrink-0 mb-2">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
          </div>
          <h3 className="text-[20px] font-medium text-[#1F2241]">
            Convert to Lead
          </h3>
          <p className="text-[14px] text-[#868686] leading-normal font-normal">
            Are you sure you want to convert this contact into a lead? This will
            move them to your CRM inquiries for tracking.
          </p>
          <div className="flex items-center justify-center gap-3 mt-4 w-full">
            <button
              type="button"
              onClick={() => setShowConfirm(false)}
              disabled={convertMutation.isPending}
              className="h-10 px-6 rounded-[12px] font-semibold text-sm flex-1 bg-[#F5F5F5] text-[#1F2241] hover:bg-[#EAEAEA] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                setShowConfirm(false);
                toast.promise(convertMutation.mutateAsync(), {
                  loading: 'Converting contact to lead...',
                  success: 'Contact successfully converted to lead!',
                  error: (error) =>
                    error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    'Failed to convert contact to lead',
                });
              }}
              disabled={convertMutation.isPending}
              className="h-10 px-6 rounded-[12px] font-semibold text-sm flex-1 bg-[#50AB3D] text-white hover:bg-[#408a31] transition-colors disabled:opacity-50"
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>
    </SideDrawer>
  );
}
